import { Layers } from '@modules/core';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import warnIcon from './assets/warn_icon.svg';
import styles from './styles/InfoTooltip.module.css';

export type InfoTooltipAnchor = 'screen-center' | 'icon';

export type InfoTooltipProps = {
  children: ReactNode;
  iconSize?: number;
  ariaLabel?: string;
  className?: string;
  anchor?: InfoTooltipAnchor;
  onToggle?: (open: boolean) => void;
};

type CardPosition = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  maxWidth?: number;
  maxHeight?: number;
};

const SCREEN_PADDING = 16;
const ICON_GAP = 8;

export const InfoTooltip = ({
  children,
  iconSize = 20,
  ariaLabel = '안내',
  className,
  anchor = 'screen-center',
  onToggle,
}: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CardPosition>({});
  const containerRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (cardRef.current?.contains(target)) return;
      setOpen(false);
      onToggle?.(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [open, onToggle]);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (anchor === 'icon') {
        const iconCenterX = (rect.left + rect.right) / 2;
        const iconCenterY = (rect.top + rect.bottom) / 2;
        const next: CardPosition = {};

        if (iconCenterX < vw / 2) {
          next.left = rect.left;
          next.maxWidth = vw - rect.left - SCREEN_PADDING;
        } else {
          next.right = vw - rect.right;
          next.maxWidth = rect.right - SCREEN_PADDING;
        }

        if (iconCenterY < vh / 2) {
          next.top = rect.bottom + ICON_GAP;
          next.maxHeight = vh - rect.bottom - ICON_GAP - SCREEN_PADDING;
        } else {
          next.bottom = vh - rect.top + ICON_GAP;
          next.maxHeight = rect.top - ICON_GAP - SCREEN_PADDING;
        }

        setPosition(next);
      } else {
        setPosition({
          top: rect.bottom + ICON_GAP,
          maxHeight: vh - rect.bottom - ICON_GAP - SCREEN_PADDING,
        });
      }
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchor]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      onToggle?.(next);
      return next;
    });
  };

  const containerClass = className ? `${styles.container} ${className}` : styles.container;
  const cardClass = anchor === 'screen-center' ? `${styles.card} ${styles.cardScreenCenter}` : styles.card;

  const card = open && (
    <span ref={cardRef} role="tooltip" className={cardClass} style={{ ...position, zIndex: Layers.UI_MODAL }}>
      {children}
    </span>
  );

  return (
    <span ref={containerRef} className={containerClass}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.iconButton}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={toggle}>
        <img src={warnIcon} width={iconSize} height={iconSize} alt="" />
      </button>
      {card && typeof document !== 'undefined' && createPortal(card, document.body)}
    </span>
  );
};
